/**
 * Time utility functions for Reels video editor
 */

/**
 * Convert seconds to timecode format (HH:MM:SS:FF)
 */
export function secondsToTimecode(seconds, frameRate = 30) {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '00:00:00:00';

  const totalFrames = Math.floor(seconds * frameRate);
  const frames = totalFrames % frameRate;
  const totalSeconds = Math.floor(seconds);
  const secs = totalSeconds % 60;
  const mins = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);

  return [
    hours.toString().padStart(2, '0'),
    mins.toString().padStart(2, '0'),
    secs.toString().padStart(2, '0'),
    frames.toString().padStart(2, '0'),
  ].join(':');
}

/**
 * Convert timecode to seconds
 */
export function timecodeToSeconds(timecode, frameRate = 30) {
  if (!timecode || typeof timecode !== 'string') return 0;

  const parts = timecode.split(':').map(Number);
  if (parts.some(isNaN)) return 0;

  const [hours = 0, mins = 0, secs = 0, frames = 0] = parts;
  return hours * 3600 + mins * 60 + secs + frames / frameRate;
}

/**
 * Format duration for display (e.g., "1:23")
 */
export function formatDuration(seconds) {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00';

  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Format duration with milliseconds (e.g., "1:23.456")
 */
export function formatDurationPrecise(seconds) {
  if (typeof seconds !== 'number' || isNaN(seconds)) return '0:00.000';

  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  const wholeSeconds = Math.floor(secs);
  const ms = Math.round((secs - wholeSeconds) * 1000);

  return `${mins}:${wholeSeconds.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
}

/**
 * Convert time to pixels based on zoom level
 */
export function timeToPixels(time, zoom, pixelsPerSecond = 50) {
  return time * pixelsPerSecond * zoom;
}

/**
 * Convert pixels to time based on zoom level
 */
export function pixelsToTime(pixels, zoom, pixelsPerSecond = 50) {
  return pixels / (pixelsPerSecond * zoom);
}

/**
 * Snap time to nearest frame
 */
export function snapToFrame(time, frameRate = 30) {
  const frame = Math.round(time * frameRate);
  return frame / frameRate;
}

/**
 * Snap time to grid
 */
export function snapToGrid(time, gridSize = 0.5) {
  return Math.round(time / gridSize) * gridSize;
}

/**
 * Check if two time ranges overlap
 */
export function rangesOverlap(start1, end1, start2, end2) {
  return start1 < end2 && end1 > start2;
}

/**
 * Get time at x position in timeline
 */
export function getTimeAtPosition(x, scrollLeft, zoom, pixelsPerSecond = 50) {
  return pixelsToTime(x + scrollLeft, zoom, pixelsPerSecond);
}
